"use strict";
/**
 * AVR 8 CPU data structures
 * Part of AVR8js
 *
 * Copyright (C) 2019, Uri Shaked
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CPU = void 0;
const interrupt_1 = require("./interrupt");
const registerSpace = 0x100;
class CPU {
    constructor(progMem, sramBytes = 8192) {
        this.progMem = progMem;
        this.sramBytes = sramBytes;
        this.data = new Uint8Array(this.sramBytes + registerSpace);
        this.data16 = new Uint16Array(this.data.buffer);
        this.dataView = new DataView(this.data.buffer);
        this.progBytes = new Uint8Array(this.progMem.buffer);
        this.readHooks = [];
        this.writeHooks = [];
        this.pendingInterrupts = [];
        this.clockEvents = [];
        this.pc22Bits = this.progBytes.length > 0x20000;
        // This lets the Timer Compare output override GPIO pins:
        this.gpioTimerHooks = [];
        this.pc = 0;
        this.cycles = 0;
        this.nextInterrupt = -1;
        this.nextClockEvent = 0;
        this.reset();
    }
    reset() {
        this.data.fill(0);
        this.SP = this.data.length - 1;
        this.pendingInterrupts.splice(0, this.pendingInterrupts.length);
        this.nextInterrupt = -1;
    }
    readData(addr) {
        if (addr >= 32 && this.readHooks[addr]) {
            return this.readHooks[addr](addr);
        }
        return this.data[addr];
    }
    writeData(addr, value) {
        const hook = this.writeHooks[addr];
        if (hook) {
            if (hook(value, this.data[addr], addr)) {
                return;
            }
        }
        this.data[addr] = value;
    }
    get SP() {
        return this.dataView.getUint16(93, true);
    }
    set SP(value) {
        this.dataView.setUint16(93, value, true);
    }
    get SREG() {
        return this.data[95];
    }
    get interruptsEnabled() {
        return this.SREG & 0x80 ? true : false;
    }
    updateNextInterrupt() {
        this.nextInterrupt = this.pendingInterrupts.findIndex((item) => !!item);
    }
    setInterruptFlag(interrupt) {
        const { flagRegister, flagMask, enableRegister, enableMask } = interrupt;
        if (interrupt.constant) {
            this.data[flagRegister] &= ~flagMask;
        }
        else {
            this.data[flagRegister] |= flagMask;
        }
        if (this.data[enableRegister] & enableMask) {
            this.queueInterrupt(interrupt);
        }
    }
    updateInterruptEnable(interrupt, registerValue) {
        const { enableMask, flagRegister, flagMask } = interrupt;
        if (registerValue & enableMask) {
            if (this.data[flagRegister] & flagMask) {
                this.queueInterrupt(interrupt);
            }
        }
        else {
            this.clearInterrupt(interrupt, false);
        }
    }
    queueInterrupt(interrupt) {
        this.pendingInterrupts[interrupt.address] = interrupt;
        this.updateNextInterrupt();
    }
    clearInterrupt({ address, flagRegister, flagMask }, clearFlag = true) {
        delete this.pendingInterrupts[address];
        if (clearFlag) {
            this.data[flagRegister] &= ~flagMask;
        }
        this.updateNextInterrupt();
    }
    clearInterruptByFlag(interrupt, registerValue) {
        const { flagRegister, flagMask } = interrupt;
        if (registerValue & flagMask) {
            this.data[flagRegister] &= ~flagMask;
            this.clearInterrupt(interrupt);
        }
    }
    addClockEvent(callback, cycles) {
        const entry = { cycles: this.cycles + Math.max(1, cycles), callback };
        // Add the new entry while keeping the array sorted
        const { clockEvents } = this;
        if (!clockEvents.length || clockEvents[clockEvents.length - 1].cycles <= entry.cycles) {
            clockEvents.push(entry);
        }
        else if (clockEvents[0].cycles >= entry.cycles) {
            clockEvents.unshift(entry);
        }
        else {
            for (let i = 1; i < clockEvents.length; i++) {
                if (clockEvents[i].cycles >= entry.cycles) {
                    clockEvents.splice(i, 0, entry);
                    break;
                }
            }
        }
        this.nextClockEvent = this.clockEvents[0].cycles;
        return callback;
    }
    updateClockEvent(callback, cycles) {
        if (this.clearClockEvent(callback)) {
            this.addClockEvent(callback, cycles);
            return true;
        }
        return false;
    }
    clearClockEvent(callback) {
        var _a, _b;
        const index = this.clockEvents.findIndex((item) => item.callback === callback);
        if (index >= 0) {
            this.clockEvents.splice(index, 1);
            this.nextClockEvent = (_b = (_a = this.clockEvents[0]) === null || _a === void 0 ? void 0 : _a.cycles) !== null && _b !== void 0 ? _b : 0;
            return true;
        }
        return false;
    }
    tick() {
        var _a, _b, _c;
        const { nextClockEvent, clockEvents } = this;
        if (nextClockEvent && nextClockEvent <= this.cycles) {
            (_a = clockEvents.shift()) === null || _a === void 0 ? void 0 : _a.callback();
            this.nextClockEvent = (_c = (_b = clockEvents[0]) === null || _b === void 0 ? void 0 : _b.cycles) !== null && _c !== void 0 ? _c : 0;
        }
        const { nextInterrupt } = this;
        if (this.interruptsEnabled && nextInterrupt >= 0) {
            const interrupt = this.pendingInterrupts[nextInterrupt];
            interrupt_1.avrInterrupt(this, interrupt.address);
            if (!interrupt.constant) {
                this.clearInterrupt(interrupt);
            }
        }
    }
}
exports.CPU = CPU;
